import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import { Icon } from "@iconify/react";
import { Button, Checkbox, Col, Form, Input, message, Row, Space, Typography } from "antd";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import { isBoolean } from "lodash";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { post } from "~/Services";
import { allowedDomainsAll } from "~/Utils";
import { Roles } from "~/Utils/Roles";
import styles from "./Dedicated.module.scss";

interface FormItems {
  userName: string;
  userEmail: string;
  ERAMAN_CREATOR: boolean;
  MAGP_CREATOR: boolean;
  APSS_CREATOR: boolean;
  EMPLOYEEAPP_CREATOR: boolean;
  // MAHBCOMM_CREATOR: boolean;
  PASSENGER_CREATOR: boolean;
  MAHB_CORPORATE_CREATOR: boolean;
  ERAMAN_REVIEWER: boolean;
  MAGP_REVIEWER: boolean;
  APSS_REVIEWER: boolean;
  EMPLOYEEAPP_REVIEWER: boolean;
  // MAHBCOMM_REVIEWER: boolean;
  PASSENGER_REVIEWER: boolean;
  MAHB_CORPORATE_REVIEWER: boolean;
  PASSENGER_ECMS_CREATOR: boolean;
  PASSENGER_ECMS_REVIEWER: boolean;
}

const defaultValue = {
  userName: "",
  userEmail: "",
  ERAMAN_CREATOR: false,
  MAGP_CREATOR: false,
  APSS_CREATOR: false,
  EMPLOYEEAPP_CREATOR: false,
  // MAHBCOMM_CREATOR: false,
  PASSENGER_CREATOR: false,
  MAHB_CORPORATE_CREATOR: false,
  ERAMAN_REVIEWER: false,
  MAGP_REVIEWER: false,
  APSS_REVIEWER: false,
  EMPLOYEEAPP_REVIEWER: false,
  // MAHBCOMM_REVIEWER: false,
  PASSENGER_REVIEWER: false,
  MAHB_CORPORATE_REVIEWER: false,
  PASSENGER_ECMS_CREATOR: false,
  PASSENGER_ECMS_REVIEWER: false,
} as FormItems;

const EqualCol = ({ children }: { children: ReactNode }) => (
  <Col
    xs={24}
    md={12}
  >
    {children}
  </Col>
);

export const accessMap: any = {
  ERAMAN: [
    { role: "ERAMAN_CREATOR", label: "Eraman Creator" },
    { role: "ERAMAN_REVIEWER", label: "Eraman Reviewer" },
  ],
  MAGP: [
    { role: "MAGP_CREATOR", label: "MAGP Creator" },
    { role: "MAGP_REVIEWER", label: "MAGP Reviewer" },
  ],
  APSS: [
    { role: "APSS_CREATOR", label: "APSS Creator" },
    { role: "APSS_REVIEWER", label: "APSS Reviewer" },
  ],
  EMPLOYEEAPP: [
    { role: "EMPLOYEEAPP_CREATOR", label: "Employee App Creator" },
    { role: "EMPLOYEEAPP_REVIEWER", label: "Employee App Reviewer" },
  ],
  // MAHBCOMM: [
  //   { role: "MAHBCOMM_CREATOR", label: "MAHBCOMM Creator" },
  //   { role: "MAHBCOMM_REVIEWER", label: "MAHBCOMM Reviewer" },
  // ],
  PASSENGER: [
    { role: "PASSENGER_CREATOR", label: "Passenger Butterfly Effect Creator" },
    { role: "PASSENGER_REVIEWER", label: "Passenger Butterfly Effect Reviewer" },
    { role: "PASSENGER_ECMS_CREATOR", label: "Passenger ECMS Creator" },
    { role: "PASSENGER_ECMS_REVIEWER", label: "Passenger ECMS Reviewer" },
  ],
  MAHBCORP: [
    { role: "MAHB_CORPORATE_CREATOR", label: "MAHBCORP Creator" },
    { role: "MAHB_CORPORATE_REVIEWER", label: "MAHBCORP Reviewer" },
  ],
  CENTRALIZED: [
    { role: "CENTRALIZED_CREATOR", label: "CENTRALIZED Creator" },
    { role: "CENTRALIZED_REVIEWER", label: "CENTRALIZED Reviewer" },
  ],
};

const allAccess = accessMap.ERAMAN.concat(accessMap.MAGP)
  .concat(accessMap.APSS)
  .concat(accessMap.EMPLOYEEAPP)
  .concat(accessMap.PASSENGER)
  .concat(accessMap.MAHBCORP)
  .concat(accessMap.CENTRALIZED);

export const AddNewDedicatedUser = () => {
  const [loading, setLoading] = useState(false);
  const [successLoader, setSuccessLoader] = useState(false);
  const { user } = useAuth();

  const navigate = useNavigate();

  const accessList = user?.role === Roles.SUPER_ADMIN ? allAccess : accessMap[user.role.split("_")[0]];
  console.log("accessList", accessList);

  const validationSchema1 = yup.object().shape({
    userName: yup
      .string()
      .trim("Name cannot include leading and trailing spaces")
      .strict(true)
      .required("Name is required"),
    userEmail: yup
      .string()
      .required("Email required")
      .lowercase("Email address must be lowercase")
      .email("Email address must be a valid email")
      .strict(true)
      .matches(
        new RegExp(`^[a-zA-Z0-9._%+-]+@(${allowedDomainsAll.join("|")})$`),
        `Permitted Domains: ${allowedDomainsAll.join(", ")}`
      ),
  });

  const onSubmit = async (values: any) => {
    const roles = [];
    for (const [key, value] of Object.entries(values)) {
      if (value && isBoolean(value)) {
        roles.push(key);
      }
    }
    if (roles.length == 0) {
      message.info("Atleast a single role must be assigned to the user for Onboarding");
      return;
    }
    const payload = {
      name: values.userName,
      email: values.userEmail,
      division: "Dedicated",
      status: "active",
      requestRoles: roles,
    };
    setLoading(true);
    post(`user/portal`, payload)
      .then((res) => {
        setSuccessLoader(true);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  return (
    <ContentWrapper
      customTitle={
        <Space align="start">
          <Icon
            onClick={() => navigate(-1)}
            icon="akar-icons:arrow-left"
            style={{
              fontSize: "1.8rem",
              cursor: "pointer",
              color: styles.primary,
              marginRight: styles.whitespace1,
            }}
          />
          <span>
            <Typography.Title
              level={5}
              style={{ margin: 0 }}
            >
              Assign Role to User
            </Typography.Title>
          </span>
        </Space>
      }
    >
      <Formik
        initialValues={defaultValue}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validationSchema1}
      >
        {(props) => {
          return (
            <Form
              layout="vertical"
              onFinish={props.handleSubmit}
            >
              <Row style={{ marginTop: 16, display: "block", width: "100%" }}>
                <Col
                  xs={24}
                  md={16}
                >
                  {
                    <>
                      <Row gutter={16}>
                        {
                          <>
                            <EqualCol>
                              <Form.Item label="User Name">
                                <Field name={`userName`}>{({ field }: FieldProps) => <Input {...field} />}</Field>
                                <Typography.Text type="danger">
                                  <ErrorMessage name={`userName`} />
                                </Typography.Text>
                              </Form.Item>
                            </EqualCol>
                            <EqualCol>
                              <Form.Item label="User Email">
                                <Field name={`userEmail`}>{({ field }: FieldProps) => <Input {...field} />}</Field>
                                <Typography.Text type="danger">
                                  <ErrorMessage name={`userEmail`} />
                                </Typography.Text>
                              </Form.Item>
                            </EqualCol>
                          </>
                        }
                      </Row>
                      <Row gutter={16}>
                        <Col md={24}>
                          <Typography.Title
                            level={5}
                            style={{ marginBottom: 10 }}
                          >
                            Choose the access you want
                          </Typography.Title>
                        </Col>
                        {
                          <>
                            {accessList &&
                              accessList.map((item: any) => (
                                <Col md={24}>
                                  <Form.Item>
                                    <Field name={item.role}>
                                      {({ field }: FieldProps) => (
                                        <>
                                          <Checkbox {...field}>{item.label}</Checkbox>
                                        </>
                                      )}
                                    </Field>
                                  </Form.Item>
                                </Col>
                              ))}
                          </>
                        }
                      </Row>
                    </>
                  }
                </Col>
              </Row>
              <Row>
                <Col
                  xs={16}
                  md={16}
                  style={{ textAlign: "right" }}
                >
                  <Space>
                    <Button
                      htmlType="submit"
                      type="primary"
                      loading={loading}
                    >
                      Submit
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
      <SuccessModal
        redirect={-1}
        title={"Successfully Created User"}
        message={"You have Successfully Created a new user"}
        type={"success"}
        buttonText={"Go Back"}
        open={successLoader}
        setOpen={setSuccessLoader}
      />
    </ContentWrapper>
  );
};
